import { useQueries, useQuery } from '@tanstack/react-query'

import { OperatorResponse } from '@/modules/api/openapi/models/OperatorResponse'
import { queryClientInstance } from '@/modules/api/queryClient'
import { internalApi } from '@/modules/api/request'
import { OpenApiQueryParamsOmitRegionId } from '@/modules/api/types'
import { getOperatorDisplayName } from '@/modules/operator'
import { useCurrentRegion } from '@/modules/urlRouting/hooks'

const getOperatorBySlugQueryParams = (slug: string | null | undefined) => ({
  queryKey: [`/operators/${slug}/`],
  queryFn: async () => await internalApi.operators.getOperatorBySlug({ slug: slug! }),
  enabled: !!slug,
})

export const useOperator = (slug: string | null | undefined) =>
  useQuery(getOperatorBySlugQueryParams(slug))

export const useOperators = (
  searchParams: OpenApiQueryParamsOmitRegionId<
    typeof internalApi.mobility.getOperatorsForRegion
  > = {
    orderBy: 'slug',
    page: 1,
    size: 50,
  }
) => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  return useQuery({
    queryKey: [`/regions/${regionId}/operators`, searchParams],
    queryFn: async () => {
      const operatorsResponse = await internalApi.mobility.getOperatorsForRegion({
        regionId,
        ...searchParams,
      })

      operatorsResponse.items.forEach(operator =>
        queryClientInstance.setQueryData<OperatorResponse>([`/operator/${operator.slug}`], operator)
      )

      return operatorsResponse
    },
  })
}

export const useMdsOperators = (
  searchParams: OpenApiQueryParamsOmitRegionId<
    typeof internalApi.mobility.getMdsOperatorsForRegion
  > = {
    orderBy: 'slug',
    page: 1,
    size: 50,
  }
) => {
  const {
    data: { regionId },
  } = useCurrentRegion()

  return useQuery({
    queryKey: [`/regions/${regionId}/mobility/mds_operators`, searchParams],
    queryFn: async () => {
      const operatorsResponse = await internalApi.mobility.getMdsOperatorsForRegion({
        regionId,
        ...searchParams,
      })

      operatorsResponse.items.forEach(operator =>
        queryClientInstance.setQueryData<OperatorResponse>([`/operator/${operator.slug}`], operator)
      )

      return operatorsResponse
    },
  })
}

export const useOperatorsBySlug = (slugs: string[]) => {
  return useQueries({
    queries: slugs.map(slug => getOperatorBySlugQueryParams(slug)),
  })
}

export const useOperatorName = (slug: string) => {
  const { data: operator } = useOperator(slug)
  return getOperatorDisplayName(operator)
}

// Useful for when you need data for individual operators but can't use useOperator
export const useOperatorLookup = () => {
  const { data: operators } = useOperators()
  return (
    operators?.items.reduce(
      (acc, operator) => {
        acc[operator.slug] = operator
        return acc
      },
      {} as { [key: string]: OperatorResponse }
    ) ?? {}
  )
}

// Useful for live map components that need data for operators configured for the live map
export const useLiveMapOperatorLookup = () => {
  const { data: operators } = useOperators({
    orderBy: 'slug',
    page: 1,
    size: 50,
    forLiveMap: true,
  })
  return (
    operators?.items.reduce(
      (acc, operator) => {
        acc[operator.slug] = operator
        return acc
      },
      {} as { [key: string]: OperatorResponse }
    ) ?? {}
  )
}

export const useOperatorNames = (slugs: string[]) => {
  const operatorLookup = useOperatorLookup()
  return slugs.map(slug => getOperatorDisplayName(operatorLookup[slug]))
}

export const useOperatorColors = (slugs: string[]) => {
  const operatorLookup = useOperatorLookup()
  return slugs.map(slug => `#${operatorLookup[slug]?.colorHex ?? '999999'}`)
}
